import type {
  ConnectionFrontend,
  HomeStateSync,
  ShopFrontend,
} from "../../typings/types"
import {
  ConnectionStatus,
  ConnectionTrigger,
  PageRoute,
  SyncDirection,
} from "../../typings/types"
import { Layout, Link, Page, Text } from "@shopify/polaris"
import { ShopContext, userLoggedInFetch } from "../App"
import { Trans, useTranslation } from "react-i18next"
import { confirmSubscriptionUrl, modifyConnectionUrl } from "../helpers/api"
import { useContext, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import Confetti from "react-confetti"
import { createCountdown } from "../helpers/misc"
import { useAppBridge } from "@shopify/app-bridge-react"

const ConfirmPayment = () => {
  const { t } = useTranslation("confirm")
  const app = useAppBridge()
  const fetch = userLoggedInFetch(app)
  const [shop, setShop] = useContext(ShopContext)
  const [automatic, setAutomatic] = useState<boolean>(false)
  const [inviteUrl, setInviteUrl] = useState<string>()
  const location = useLocation()
  const [queryParams] = useSearchParams()
  const navigate = useNavigate()
  const width = window.innerWidth
  const height = window.innerHeight
  const [countdownSeconds, setCountdownSeconds] = useState<number>(10)
  const homeParams = useRef<HomeStateSync>()
  let parentConnection: ConnectionFrontend | undefined

  useEffect(() => {
    const { state } = location as {
      state: { invite: string; plan: ConnectionTrigger }
    }
    const invite = queryParams.get("invite") || state?.invite // if this shop is inviting another shop, this is that shop's domain
    if (invite) setInviteUrl(invite)
    const planParam =
      queryParams.get("plan") || (state?.plan as ConnectionTrigger | null)
    const isAutomatic = planParam === ConnectionTrigger.AUTOMATIC
    setAutomatic(isAutomatic)
    const charge_id = queryParams.get("charge_id")
    // navigate(PageRoute.CONFIRM, { replace: true })

    if (!charge_id && !planParam) {
      navigate("/")
      return
    }

    if (planParam) {
      // we're getting to this page from the pricing page
      if (isAutomatic && charge_id) {
        // confirm that this shop just subscribed to automatic syncing
        fetch(confirmSubscriptionUrl({ charge_id }), {
          method: "POST",
        })
          .then((res) => res?.text())
          .then((billing_last) => {
            if (billing_last) {
              setShop((currentShop) =>
                currentShop
                  ? { ...currentShop, billing_last, billing_active: true }
                  : currentShop
              )
            }
          })
      }

      if (isAutomatic && parentConnection) {
        // shop was changed to automatic, so run an initial sync
        homeParams.current = {
          syncConfirmation: true,
          syncDomain: parentConnection.domain,
          forceUpdate: false,
          newlyAutomatic: true,
        }

        // navigate(location.pathname, { replace: true })
      }

      if (
        parentConnection &&
        parentConnection.status === ConnectionStatus.ACTIVE
      ) {
        // connection was already active, we're just switching from manual to automatic
        setShop((prevShop) => {
          if (!prevShop) return prevShop
          const { connections } = prevShop

          // update the existing connection
          const connection = connections.find(
            (connection: ConnectionFrontend) =>
              connection.domain ===
              (parentConnection as ConnectionFrontend).domain
          )
          if (connection) {
            connection.trigger = ConnectionTrigger.AUTOMATIC
          }
          return {
            ...prevShop,
            connections: [...connections],
          }
        })
      } else {
        // create the connection between this shop and its parent
        if (invite) {
          // this is a new connection
          fetch(
            modifyConnectionUrl({
              domain: invite,
              direction: SyncDirection.FROM, // we only get to this page from child shops
            }),
            {
              method: "POST",
            }
          ).then(() => {
            setShop((prevShop) => {
              if (!prevShop) return prevShop
              const { connections } = prevShop

              const newConnection: ConnectionFrontend = {
                domain: invite,
                status: ConnectionStatus.PENDING,
                direction: SyncDirection.FROM,
                trigger: planParam as ConnectionTrigger,
              }
              connections.push(newConnection)
              return {
                ...prevShop,
                connections: [...connections],
              }
            })
          })
        } else if (parentConnection) {
          // update the existing connection
          fetch(
            modifyConnectionUrl({
              domain: parentConnection.domain,
              direction: SyncDirection.FROM, // we only get to this page from child shops
            }),
            {
              method: "PATCH",
            }
          ).then(() => {
            setShop((prevShop) => {
              if (!prevShop) return prevShop
              const { connections } = prevShop

              const connection = connections.find(
                (connection: ConnectionFrontend) =>
                  connection.domain === parentConnection?.domain
              )
              if (connection) {
                connection.status = ConnectionStatus.ACTIVE
                connection.trigger = planParam as ConnectionTrigger
              }
              return {
                ...prevShop,
                connections: [...connections],
              }
            })
          })
        }
      }
    }

    // redirect to homepage
    createCountdown(countdownSeconds, setCountdownSeconds, () =>
      navigate("/", { state: homeParams.current })
    )
  }, [])

  if (!shop) return <div>Loading</div> // this should never get reached
  parentConnection = shop.connections[0]

  return (
    <Page>
      <Layout>
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Text variant="heading2xl" as="h1">
            {t("confirm:payment.congratulations")}{" "}
            {parentConnection &&
            parentConnection.status !== ConnectionStatus.PENDING
              ? t("confirm:payment.readyForSync")
              : t("confirm:payment.nowConnected")}
          </Text>
          <br />
          <Text variant="headingLg" as="p">
            {!parentConnection ||
            parentConnection.status === ConnectionStatus.PENDING
              ? t("confirm:payment.makeSureToInstall", {
                  externalShops:
                    inviteUrl ||
                    parentConnection?.domain ||
                    t("confirm:payment.theOtherShop"),
                })
              : automatic
              ? t("confirm:payment.automatic")
              : t("confirm:payment.manual")}
          </Text>
          <br />
          <br />
          <br />
          <Text tone="subdued" as="p">
            <Trans
              i18nKey="confirm:redirect"
              countdownSeconds={countdownSeconds}
            >
              You'll be redirected to the main page within{" "}
              {{ countdownSeconds }} seconds. Or{" "}
              <Link
                onClick={() => navigate("/", { state: homeParams.current })}
              >
                click here
              </Link>
            </Trans>
          </Text>
          <Confetti
            numberOfPieces={500}
            recycle={false}
            gravity={0.05}
            friction={0.99}
            width={width}
            height={height}
          />
        </div>
      </Layout>
    </Page>
  )
}

export default ConfirmPayment
